// src/pages/onboarding/patient/steps/StepDone.jsx
import React from "react";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { canUseOnboardingDraftMode } from "@/lib/onboardingDraftMode";
import { loadPatientIntroPreview } from "@/lib/patientIntroPreview";

export default function StepDone({
  step,
  total,
  onBack,
  onVerify,
  onSkip,
  heroIcon = "/icons/patient1.svg",
  capturedPhone = "",
}) {
  const { refreshProfile, user } = useAuth();
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");

  async function requireAuthUser() {
    const { data: s } = await supabase.auth.getSession();
    const sessUser = s?.session?.user || null;
    if (sessUser?.id) return sessUser;

    const { data, error } = await supabase.auth.getUser();
    const u = data?.user || null;
    if ((error || !u?.id) && canUseOnboardingDraftMode()) return null;
    if (error || !u?.id) throw new Error("Your session isn’t ready yet. Please try again.");
    return u;
  }

  async function syncPatientProfileFromAuth() {
    const u = await requireAuthUser();
    if (!u?.id) return;
    const previewPhone = String(loadPatientIntroPreview(u.id || user?.id || "")?.phone || "").trim();
    const resolvedPhone =
      String(u.phone || "").trim() ||
      String(capturedPhone || "").trim() ||
      previewPhone;
    const { data: existingProfile } = await supabase
      .from("patient_profiles")
      .select("phone_verified_at")
      .eq("user_id", u.id)
      .maybeSingle();

    const payload = {
      user_id: u.id,
      email: u.email ?? null,
      email_verified_at: u?.email_confirmed_at || null,
      updated_at: new Date().toISOString(),
    };

    if (resolvedPhone) {
      payload.phone = resolvedPhone;
      payload.phone_verified_at =
        u?.phone_confirmed_at || existingProfile?.phone_verified_at || null;
    }

    const { error } = await supabase
      .from("patient_profiles")
      .upsert(payload, { onConflict: "user_id" });

    if (error) throw error;

    if (typeof refreshProfile === "function") {
      await refreshProfile();
    }
  }

  async function runAndContinue(nextAction) {
    if (saving) return;
    setErr("");
    setSaving(true);

    try {
      await syncPatientProfileFromAuth();
      await Promise.resolve(nextAction?.());
    } catch (e) {
      setErr(e?.message || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        header={null}
        center
        bodyClassName="flex-1 px-4 !min-h-[calc(100dvh-var(--onb-footer-h)-120px)] md:!min-h-[calc(100dvh-var(--onb-footer-h)-170px)]"
        footer={
          <div className="space-y-3 relative z-[60] pointer-events-auto">
            <Button
              type="button"
              fullWidth
              className="pointer-events-auto"
              loading={saving}
              disabled={saving}
              onClick={() => runAndContinue(onVerify)}
            >
              Verify information
            </Button>

            <Button
              type="button"
              fullWidth
              variant="secondary"
              className="pointer-events-auto bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))] hover:bg-[rgba(77,163,255,0.18)]"
              loading={saving}
              disabled={saving}
              onClick={() => runAndContinue(onSkip)}
            >
              Skip for now
            </Button>

            {!!err && (
              <p className="mt-2 text-body-small text-[rgb(var(--afrimed-danger))] text-center">
                {err}
              </p>
            )}
          </div>
        }
      >
        {/* TRUE center: no pt-* nudges */}
        <div className="w-full max-w-[640px] mx-auto flex flex-col items-center">
          <img
            src={heroIcon}
            alt=""
            className="w-[126px] h-[126px] mb-6 sm:mb-8 select-none"
            draggable="false"
          />

          <h2 className="text-title-large text-center mb-5 text-on-surface">
            <span className="block">Your health, your way,</span>
            <span className="block">anytime, anywhere.</span>
          </h2>

          <div className="w-full max-w-[520px] space-y-5 text-body-large text-on-surface-var">
            <Bullet
              icon="/icons/list-items/doctor-01.svg"
              text="Connect with qualified doctors for secure virtual or in-person consultations."
            />
            <Bullet
              icon="/icons/list-items/hospital.svg"
              text="Access care through trusted hospitals and clinics for referrals, diagnostics, and ongoing support."
            />
            <Bullet
              icon="/icons/list-items/personal_injury.svg"
              text="Take control of your health with easy access to prescriptions, medical records, and real-time updates on insurance claims."
            />
          </div>
        </div>
      </OnboardingSection>
    </div>
  );
}

function Bullet({ icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <img src={icon} alt="" className="w-6 h-6 shrink-0 mt-0.5" />
      <p className="tracking-[0.005em] text-on-surface-var">{text}</p>
    </div>
  );
}
